import { OfferItem, VIP_CRESTS, VipCrest, parseOfferItem } from "./offers";

/**
 * VIP v2 (docs/SHOP.md §8; balance/src/vip.json; owner decisions 2026-09-26): 12 levels like Kingshot, VIP XP from
 * purchases and a growing daily claim streak, and per-level benefits that include COMBAT power (tower damage, fortress
 * health, troop strength), build speed and resource production besides the pass-1 quality-of-life perks, free Mystery
 * Merchant refreshes, a crest and a free daily bundle. Parsed from the generated bundle's `vip` section; the rules
 * that read it are meta/vipLevels.ts. An older bundle has no section: `emptyVipTable` has a single level 0 of zeros.
 */

/** The benefits of one VIP level. Every number is cumulative (gen.py checks none falls from one level to the next). */
export interface VipBenefits {
  /** Whole percents added to every tower's damage (the fortress's own tower included), through GearBonus.damage. */
  readonly towerDamagePercent: number;
  /** Whole percents added to the fortress's health, through the base's GearBonus.hp. */
  readonly fortressHpPercent: number;
  /** Whole percents added to the fortress's troops' health and damage (VIP-CORE: a GearBonus field hashed only when nonzero). */
  readonly troopPercent: number;
  /** Upgrade timers are divided by (1 + this / 100). */
  readonly buildSpeedPercent: number;
  /** Tribute and battle-chest resources × (1 + this / 100) (paid after the chest's hashed roll; never purchases). */
  readonly resourcePercent: number;
  readonly tributeExtraHours: number;
  readonly extraReductionsPerDay: number;
  /** A running upgrade with at most this many minutes left can be finished free. */
  readonly freeFinishMinutes: number;
  readonly extraOrderRerolls: number;
  readonly extraDealChanges: number;
  readonly extraQueuePerBuilder: number;
  readonly extraCrateSlots: number;
  /** Mystery Merchant refreshes a UTC day before the gem-priced ones (balance/src/shops.json merchant.refreshGems). */
  readonly freeMerchantRefreshes: number;
  readonly crest: VipCrest;
  /** The free daily bundle, claimed once a UTC day from the VIP page. */
  readonly dailyBundle: readonly OfferItem[];
}

/** One VIP level: its cumulative XP threshold and its benefits. */
export interface VipTierDef extends VipBenefits { readonly xp: number }

export interface VipStreakDef {
  /** [streak day, XP] points, streak days rising; XP is linear between them and flat after the last. */
  readonly anchors: readonly (readonly [number, number])[];
}

export interface VipTable {
  /** VIP XP per whole price dollar of a purchase (the bubble). */
  readonly xpPerDollar: number;
  readonly streak: VipStreakDef;
  /** Index = VIP level; level 0 is 0 XP. */
  readonly levels: readonly VipTierDef[];
}

/** The number keys of a level, in vip.json's names (gen.py's VIP2_NUMBERS is the same list). */
export const VIP_NUMBER_KEYS = [
  "towerDamagePercent", "fortressHpPercent", "troopPercent", "buildSpeedPercent", "resourcePercent", "tributeExtraHours", "extraReductionsPerDay",
  "freeFinishMinutes", "extraOrderRerolls", "extraDealChanges", "extraQueuePerBuilder", "extraCrateSlots", "freeMerchantRefreshes",
] as const satisfies readonly (keyof VipBenefits)[];

/** A level of nothing: VIP 0, and every level of a bundle without VIP. */
export const NO_VIP_BENEFITS: VipBenefits = {
  towerDamagePercent: 0, fortressHpPercent: 0, troopPercent: 0, buildSpeedPercent: 0, resourcePercent: 0, tributeExtraHours: 0,
  extraReductionsPerDay: 0, freeFinishMinutes: 0, extraOrderRerolls: 0, extraDealChanges: 0, extraQueuePerBuilder: 0, extraCrateSlots: 0,
  freeMerchantRefreshes: 0, crest: "none", dailyBundle: [],
};

export function emptyVipTable(): VipTable {
  return { xpPerDollar: 0, streak: { anchors: [[1, 0]] }, levels: [{ ...NO_VIP_BENEFITS, xp: 0 }] };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= 0) return v;
  throw new TypeError(`${where}: expected a whole number, got ${JSON.stringify(v)}`);
}

function parseTier(l: Json, i: number): VipTierDef {
  const where = `vip.json level ${i}`;
  const crest = (l.crest ?? "none") as VipCrest;
  if (!VIP_CRESTS.includes(crest)) throw new Error(`${where}: unknown crest '${crest}'`);
  const numbers = Object.fromEntries(VIP_NUMBER_KEYS.map((k) => [k, l[k] === undefined ? 0 : int(l[k], `${where} ${k}`)])) as Record<(typeof VIP_NUMBER_KEYS)[number], number>;
  const dailyBundle = ((l.dailyBundle ?? []) as Json[]).map((it, n) => parseOfferItem(it, `${where} dailyBundle[${n}]`));
  return { xp: int(l.xp, where), ...numbers, crest, dailyBundle };
}

/** Reads the bundle's `vip` section (gen.py validate_vip has checked it; this re-checks shape and throws otherwise). */
export function parseVipTable(root: Json): VipTable {
  const s = root?.vip;
  if (s === undefined) return emptyVipTable();
  const anchors = (s.streak.anchors as Json[]).map((a, i): [number, number] => [int(a[0], `vip.json streak anchor ${i}`), int(a[1], `vip.json streak anchor ${i}`)]);
  if (anchors.length === 0) throw new Error("vip.json streak: no anchors");
  const levels = (s.levels as Json[]).map(parseTier);
  if (levels.length === 0 || levels[0].xp !== 0) throw new Error("vip.json levels: level 0 must start at 0 XP");
  return { xpPerDollar: int(s.xpPerDollar, "vip.json xpPerDollar"), streak: { anchors }, levels };
}
